import { useState } from "react";
import { motion } from "framer-motion";
import {
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const codes = {
    S: { WORM: "8588", YETI: "5785", MOTH: "8587", CRAB: "7857" },
    M: { WORM: "7671", YETI: "3576", MOTH: "1676", CRAB: "5775" },
    NI: { WORM: "5861", YETI: "5482", MOTH: "1888", CRAB: "4664" },
    OUY: { WORM: "9377", YETI: "3192", MOTH: "7394", CRAB: "9729" },
};

type IndicatorKey = keyof typeof codes;
type WordKey = keyof typeof codes[IndicatorKey];

const indicators: IndicatorKey[] = ["S", "M", "NI", "OUY"];
const words: WordKey[] = ["WORM", "YETI", "MOTH", "CRAB"];

export default function SvCodeCracker({ darkMode }: { darkMode: boolean }) {
    const [indicator, setIndicator] = useState<IndicatorKey | "">("");
    const [word, setWord] = useState<WordKey | "">("");
    const [password, setPassword] = useState("");
    const [copied, setCopied] = useState(false);

    const handleSubmit = () => {
        if (
            indicator && word &&
            Object.prototype.hasOwnProperty.call(codes, indicator) &&
            Object.prototype.hasOwnProperty.call(codes[indicator], word)
        ) {
            setPassword(codes[indicator as IndicatorKey][word as WordKey]);
            setCopied(false);
        } else {
            console.warn("Invalid indicator or word selection");
        }
    };

    const handleCopy = () => {
        if (password) {
            void navigator.clipboard.writeText(password);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const getButtonStyle = (selected: boolean, color: string) => {
        const base = "rounded-lg px-2 py-1 border font-medium transition-colors duration-200";

        const selectedStyles: Record<string, string> = {
            indigo: "bg-indigo-600 text-white border-indigo-700",
            purple: "bg-purple-600 text-white border-purple-700",
        };

        const unselectedStyles: Record<string, string> = {
            indigo: "bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-100 hover:text-indigo-900",
            purple: "bg-white text-purple-700 border-purple-300 hover:bg-purple-100 hover:text-purple-900",
        };

        return `${base} ${selected ? selectedStyles[color] : unselectedStyles[color]}`;
    };

    const toggleSelection = (current: string, value: string) => (current === value ? "" : value);

    return (
        <div
            className={`min-h-screen w-full h-full ${
                darkMode
                    ? "bg-gray-900 text-white"
                    : "bg-gradient-to-br from-indigo-300 via-purple-300 to-pink-200 text-gray-800"
            } flex items-center justify-center p-4`}
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className={`w-full max-w-md p-6 rounded-2xl backdrop-blur shadow-xl ${
                    darkMode ? "bg-gray-800 text-white" : "bg-white/80"
                }`}
            >
                <CardHeader>
                    <CardTitle className="text-center text-3xl font-bold">
                        Shattered Veil Decoder
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-sm font-semibold mb-2">Bottom Left Indicator</h3>
                            <div className="grid grid-cols-4 gap-2">
                                {indicators.map((ind) => (
                                    <button
                                        key={ind}
                                        onClick={() => setIndicator(toggleSelection(indicator, ind) as IndicatorKey | "")}
                                        className={getButtonStyle(indicator === ind, "indigo")}
                                    >
                                        {ind}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold mb-2">Code Word</h3>
                            <div className="grid grid-cols-4 gap-2">
                                {words.map((w) => (
                                    <button
                                        key={w}
                                        onClick={() => setWord(toggleSelection(word, w) as WordKey | "")}
                                        className={getButtonStyle(word === w, "purple")}
                                    >
                                        {w}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <Button
                            className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold shadow-lg hover:scale-105 transition-transform duration-200"
                            onClick={handleSubmit}
                        >
                            Decode Password
                        </Button>

                        {password && (
                            <div className="flex flex-col items-center gap-2 mt-4">
                                <div
                                    className={`text-xl font-bold py-3 px-4 rounded-lg ${
                                        darkMode
                                            ? "bg-gray-700 text-purple-300"
                                            : "bg-purple-100 text-purple-800"
                                    } border border-purple-300 shadow-inner animate-fade-in`}
                                >
                                    Password: <span>{password}</span>
                                </div>
                                <Button
                                    onClick={handleCopy}
                                    variant="secondary"
                                    className="hover:scale-105 transition-transform duration-200"
                                >
                                    {copied ? "Copied!" : "Copy to Clipboard"}
                                </Button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </motion.div>
        </div>
    );
}